import { Chip, getContrastRatio, makeStyles } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { TextField } from "app/components/common/TextField";
import { pad } from "lodash";
import React, { memo, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getContrastText } from "util/contrastColor";
/* -------------------------------------------------------------------------- */
/**
 * @options {id, value}
 */
export const TagSelector = memo(
  ({
    label,
    onChange,
    value,
    options,
    onSearch,
    selected = [],
    disabled,
    error,
    helperText,
    required,
    className,
    disabledOptions,
    // A caller passing onInputChange wants to drive a live search (see
    // StandbyAssignmentModal's Crew field). MUI's Autocomplete only exposes
    // the text actually being typed through params.inputProps.value, never
    // a top-level prop — renderInput used to forward none of that to the
    // shared TextField below, whose own `value={value ?? ""}` prop (a
    // *different*, unrelated `value` — the generic one this component
    // doesn't otherwise use for its input) then permanently pinned the
    // field at "". Every keystroke's native DOM value got reconciled back
    // to that fixed "" on the very next render, which is what dropped/
    // scrambled characters while typing. Tracking the typed text here and
    // feeding it back in as a real controlled value fixes that for any
    // consumer of onInputChange, not just the one that first exercised it.
    onInputChange = () => {},
    // "row" (default) wraps options tag-cloud style, for short colored tags.
    // "column" lists one option per line, for longer labels or a checkbox
    // list (e.g. a custom renderOption with a checkbox per row).
    optionsLayout = "row",
    // "chips" (default) renders one chip per selected value, which grows the
    // field's height/width as more are picked. "summary" keeps the field a
    // fixed size once more than one value is selected, showing "N selected"
    // instead of individual chips (the single-selection case still shows its
    // one chip, since that alone doesn't grow the field).
    tagsDisplay = "chips",
    // "fixed" (default) shows label as a static heading above the box,
    // always visible — the original behavior every existing caller relies
    // on. "floating" instead renders it as MUI's animated InputLabel inside
    // the outline (inline when empty/unfocused, shrunk to the notch once
    // there's a selection or focus), matching the plain Selector component.
    labelVariant = "fixed",
    // When true, options that are selected at the moment the list opens are
    // listed first (in their original order), separated from the rest by a
    // divider. The order is snapshotted on open so rows don't jump around
    // while the user is checking/unchecking them. Only meaningful alongside
    // filterSelectedOptions={false}, otherwise selected options are hidden.
    selectedFirst = false,
    onOpen,
    ...rest
  }) => {
    const classes = useStyles();
    const [pinnedIds, setPinnedIds] = useState([]);
    const [typedValue, setTypedValue] = useState("");
    const handleTypedChange = (event) => {
      setTypedValue(event.target.value);
      onInputChange(event);
    };
    const orderedOptions = useMemo(() => {
      if (!selectedFirst || !pinnedIds.length) return options;
      return [
        ...options.filter((option) => pinnedIds.includes(option.id)),
        ...options.filter((option) => !pinnedIds.includes(option.id)),
      ];
    }, [options, pinnedIds, selectedFirst]);
    const handleOpen = (event) => {
      if (selectedFirst) setPinnedIds(selected ?? []);
      onOpen?.(event);
    };
    const groupBy = (option) =>
      pinnedIds.includes(option.id) ? PINNED_GROUP : REST_GROUP;
    const renderGroup = ({ key, group, children }) => (
      <li
        key={key}
        className={`${classes.group} ${
          group === REST_GROUP ? classes.restGroup : ""
        }`}
      >
        <ul className={classes.groupUl}>{children}</ul>
      </li>
    );
    function handleSearch(search) {
      onSearch(search);
    }
    // Must keep the SAME array reference across renders unless the selected
    // set actually changed — Autocomplete has its own internal effect
    // (`useEffect(() => resetInputValue(null, value), [value, ...])`) that
    // watches this exact `value` prop by reference and, in `multiple` mode,
    // always resets the typed search text back to "" whenever it fires.
    // `useMemo([options, selected])` isn't enough on its own: a caller doing
    // a live search (e.g. StandbyAssignmentModal's Crew field) replaces
    // `options` with a new array on every search result even while the
    // selection hasn't changed, which still produces a new reference here
    // and still wipes out whatever was just typed. Comparing the *content*
    // (ids, in order) against the last computed result — and only publishing
    // a new array when that content actually differs — keeps the reference
    // stable through option-list churn that doesn't touch the selection.
    const selectedOptionsRef = useRef([]);
    const nextSelectedOptions = options.filter((option) =>
      selected?.includes(option.id)
    );
    const selectedOptionsChanged =
      nextSelectedOptions.length !== selectedOptionsRef.current.length ||
      nextSelectedOptions.some(
        (option, index) => option.id !== selectedOptionsRef.current[index]?.id
      );
    if (selectedOptionsChanged)
      selectedOptionsRef.current = nextSelectedOptions;
    const selectedOptions = selectedOptionsRef.current;
    const renderLabel = (option) => {
      return `${option?.value}`;
    };
    const renderOption = (option) => (
      <Tag
        label={option.value}
        color={option.color}
        disabled={disabledOptions?.includes(option.id)}
      />
    );
    const renderTags = (tagOptions, getTagProps) => {
      if (tagsDisplay === "summary" && tagOptions.length > 1) {
        return [
          <span key="summary" className={classes.summaryTag}>
            {tagOptions.length} selected
          </span>,
        ];
      }
      return tagOptions.map((option, index) => (
        <Tag
          label={option.value}
          color={option.color}
          disabled={disabled || disabledOptions?.includes(option.id)}
          {...getTagProps({ index })}
        />
      ));
    };

    return (
      <Autocomplete
        multiple
        disabled={disabled}
        defaultValue={selectedOptions}
        value={selectedOptions}
        className={`${classes.autocomplete} ${className}`}
        classes={{
          popper: `${classes.popper} ${
            optionsLayout === "column" ? classes.popperColumn : ""
          }`,
          noOptions: classes.noOptions,
        }}
        options={orderedOptions}
        onOpen={handleOpen}
        {...(selectedFirst && { groupBy, renderGroup })}
        renderOption={renderOption}
        disableClearable={true}
        clearOnBlur={false}
        onChange={(event, newValue) => {
          let tags = newValue.map((t) =>
            !disabledOptions?.includes(t.id) ? t.id : undefined
          );
          onChange(tags);
        }}
        renderTags={renderTags}
        getOptionLabel={renderLabel}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            error={error}
            helperText={helperText ?? error}
            required={required ? required : null}
            {...params}
            className={classes.multiline}
            id="textfield"
            label={labelVariant === "floating" ? undefined : label}
            inputLabel={labelVariant === "floating" ? label : undefined}
            variant="outlined"
            value={typedValue}
            onChange={handleTypedChange}
          />
        )}
        {...rest}
      />
    );
  }
);
/* -------------------------------------------------------------------------- */
const PINNED_GROUP = "pinned";
const REST_GROUP = "rest";
/* -------------------------------------------------------------------------- */
const Tag = ({ label, color, disabled, ...rest }) => {
  const classes = useStyles({ color });
  return (
    <Chip
      variant="outlined"
      label={label}
      {...rest}
      className={classes.chip}
      disabled={disabled}
    />
  );
};
/* -------------------------------------------------------------------------- */
const useStyles = makeStyles(
  (theme) => ({
    autocomplete: {
      // minWidth: 170,
      "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
        // padding: 0,
      },
      "& .MuiAutocomplete-noOptions": {
        display: "none",
      },
    },
    popper: {
      // width: "max-content!important",
      "& .MuiAutocomplete-listbox": {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        paddinginline: theme.spacing(1),
        "& li": {
          padding: "4px 0px",
        },
        // selectedFirst groups: each spans the full width and lays out its
        // own options the same way the listbox does.
        "& $group": {
          width: "100%",
          padding: 0,
        },
        "& $restGroup:not(:first-child)": {
          borderTop: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    group: {},
    restGroup: {},
    groupUl: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      margin: 0,
      padding: 0,
      listStyle: "none",
    },
    popperColumn: {
      "& .MuiAutocomplete-listbox": {
        flexDirection: "column",
        flexWrap: "nowrap",
        "& li": {
          width: "100%",
        },
        "& $groupUl": {
          flexDirection: "column",
          flexWrap: "nowrap",
        },
      },
    },
    noOptions: {
      display: "none",
    },
    summaryTag: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(1),
      whiteSpace: "nowrap",
    },
    optionContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      flex: 1,
    },
    optionCode: { fontSize: 8, color: theme.palette.primary.main },
    chip: {
      height: theme.spacing(3),
      marginInline: "4px",
      backgroundColor: ({ color }) => color ?? "unset",
      color: ({ color }) => getContrastText(color ?? "#ffffff"),
      "& .MuiChip-label": {
        fontSize: 12,
      },
      "& .MuiChip-deleteIcon": {
        color: ({ color }) =>
          `${
            color
              ? getContrastText(color || "#ffffff")
              : theme.palette.grey["dark"]
          } !important`,
        width: theme.spacing(2),
      },
    },
    multiline: {
      "& .MuiInputBase-root": {
        height: "auto",
        minHeight: 36,
        //paddingInline: `${theme.spacing(1)}px !important`,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
      },
      "& .MuiChip-root": {
        marginTop: 0,
      },
    },
  }),
  { name: "TagSelector" }
);
